/**
 * useUsageGate (#58) — freemium usage gate, calculator doctrine.
 *
 * Counting is server-side on www.amenai.net (per-product WP endpoint,
 * rolling 7-day window keyed by nb_uid cookie + IP fallback); enforcement
 * is client-side and FAIL-OPEN: any network error, timeout, non-2xx, or
 * malformed response lets the briefing proceed — the briefer is a safety
 * tool and a backend hiccup must never lock a pilot out.
 *
 * One use = one distinct briefing target per session (session-scoped seen
 * set; re-briefing the same target burns nothing). The gate fires when the
 * server count exceeds QUOTA — the caller then routes to the interstitial.
 *
 * Every outcome is logged as ONE JSON line in the browser console —
 * `usage_gate_fired`, `usage_gate_bypassed` (once per reason per session) and
 * `usage_gate_fail_open` (with its cause). These reach a pilot's console or a
 * support session, never the operator: the operator's signal is the
 * WordPress usage-row count (README, "Freemium usage gate").
 *
 * A fail-open CLEARS the gate: the in-session flag and the persisted count
 * are dropped, so a disabled endpoint frees a previously gated user on
 * their next briefing attempt (the runbook's no-rebuild off-ramp).
 *
 * The last-known count is persisted (localStorage, rolling-window TTL) so the
 * /tracking guard can block the tracker past quota across reloads. The
 * rolling window makes an old count meaningless, hence the TTL; a stale
 * or missing state never gates (fail-open again).
 *
 * Module-singleton state: the ResultsView hook, the tracker guard, and the
 * interstitial all read the same refs.
 */
import { ref } from 'vue';
import { gateBypassReason } from '@/composables/usageGateLogic';
import { getCookie, setCookie } from '@/utils/cookies';
import {
  USAGE_ENDPOINT,
  QUOTA,
  GATE_ENABLED,
  COOKIE_UID,
  COOKIE_UID_EXPIRES_DAYS,
  USAGE_TIMEOUT_MS,
  GATE_STATE_STORAGE_KEY,
  GATE_STATE_TTL_MS
} from '@/config/usageGate';

// --- Module-singleton state ----------------------------------------------
const gateOpen = ref(false);

// One count per distinct briefing target per session. Page reload = new
// session = the set is empty again (accepted looseness for a nudge; the
// server count is what actually enforces the window).
const _seenTargetsThisSession = new Set<string>();

// Bypass reasons already logged this session — one line per reason, not one
// per briefing (a localhost dev session would otherwise spam the console).
const _loggedBypassReasons = new Set<string>();

type FailOpenCause = 'http_error' | 'malformed_body' | 'network_or_timeout';

function _logEvent(level: 'info' | 'warn', event: string, fields: Record<string, unknown>): void {
  console[level](JSON.stringify({ event, ...fields }));
}

function _clearGateState(): void {
  gateOpen.value = false;
  try {
    localStorage.removeItem(GATE_STATE_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing persisted to clear.
  }
}

function _failOpen(cause: FailOpenCause, detail: Record<string, unknown> = {}): false {
  _logEvent('warn', 'usage_gate_fail_open', { cause, ...detail });
  _clearGateState();
  return false;
}

function _generateUid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'nb-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
}

function _getOrCreateUid(): string {
  let uid = getCookie(COOKIE_UID);
  if (!uid || uid.length < 8) {
    uid = _generateUid();
    setCookie(COOKIE_UID, uid, COOKIE_UID_EXPIRES_DAYS);
  }
  return uid;
}

function _bypassReason(): string | null {
  const reason = gateBypassReason({
    gateEnabled: GATE_ENABLED,
    hostname: typeof window !== 'undefined' ? window.location.hostname : ''
  });
  if (reason !== null && !_loggedBypassReasons.has(reason)) {
    _loggedBypassReasons.add(reason);
    _logEvent('info', 'usage_gate_bypassed', { reason });
  }
  return reason;
}

// --- Persisted last-known count (for the tracker guard) -------------------
interface PersistedGateState {
  count: number;
  at: number;
}

function _loadPersistedCount(): number {
  try {
    const raw = localStorage.getItem(GATE_STATE_STORAGE_KEY);
    if (!raw) return 0;
    const state = JSON.parse(raw) as PersistedGateState;
    if (
      typeof state.count !== 'number' ||
      typeof state.at !== 'number' ||
      Date.now() - state.at > GATE_STATE_TTL_MS
    ) {
      return 0;
    }
    return state.count;
  } catch {
    return 0; // corrupt storage never gates
  }
}

function _persistCount(count: number): void {
  try {
    localStorage.setItem(
      GATE_STATE_STORAGE_KEY,
      JSON.stringify({ count, at: Date.now() } satisfies PersistedGateState)
    );
  } catch {
    // Storage full/unavailable — the tracker guard just loses cross-reload
    // memory. Fail-open by construction.
  }
}

export function useUsageGate() {
  /**
   * Records one briefing event server-side for a distinct target and returns
   * `true` if the user is over quota (caller routes to the interstitial).
   * Never throws; every failure path returns `false` (fail-open).
   */
  async function checkAndIncrement(targetKey: string): Promise<boolean> {
    const bypass = _bypassReason();
    if (bypass !== null) return false;

    const key = targetKey.trim().toUpperCase();
    if (!key) return false;
    if (_seenTargetsThisSession.has(key)) return gateOpen.value;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), USAGE_TIMEOUT_MS);
    try {
      const res = await fetch(USAGE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cookie_id: _getOrCreateUid() }),
        signal: controller.signal
      });
      if (!res.ok) return _failOpen('http_error', { status: res.status });
      let data: { count?: number };
      try {
        data = (await res.json()) as { count?: number };
      } catch {
        // A 200 carrying HTML (maintenance page, edge challenge) is a body
        // problem, not a network one — name it so the operator reads it right.
        return _failOpen('malformed_body', { status: res.status });
      }
      if (typeof data.count !== 'number' || !Number.isFinite(data.count)) {
        return _failOpen('malformed_body', { status: res.status });
      }

      // Mark the target seen only on a SUCCESSFUL count (PR #70 review): a
      // failed/timed-out POST must not both skip the count AND suppress
      // counting the same target for the rest of the session.
      _seenTargetsThisSession.add(key);
      _persistCount(data.count);

      if (data.count > QUOTA) {
        gateOpen.value = true;
        _logEvent('info', 'usage_gate_fired', { count: data.count, quota: QUOTA, target: key });
        return true;
      }
      // Quota recovery (PR #70 review): the rolling window aged out enough
      // events — lift the gate in-session instead of staying fail-closed
      // until a reload.
      gateOpen.value = false;
      return false;
    } catch (err) {
      return _failOpen('network_or_timeout', { name: (err as Error)?.name });
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Cross-reload over-quota check for the /tracking guard. Uses the
   * persisted last-known count (TTL = the rolling window); never issues a
   * network call and never burns quota. The kill-switch and the dev origin
   * win here too.
   */
  function isOverQuota(): boolean {
    if (_bypassReason() !== null) return false;
    if (gateOpen.value) return true;
    return _loadPersistedCount() > QUOTA;
  }

  /** Test hook — reset module-singleton state between specs. */
  function _resetForTests(): void {
    gateOpen.value = false;
    _seenTargetsThisSession.clear();
    _loggedBypassReasons.clear();
  }

  return { gateOpen, checkAndIncrement, isOverQuota, _resetForTests };
}
